'use client';

import { ArticleCard } from '@pickleballinc/features/src/hub-cards/ArticleCard';
import { SkeletonArticleCard } from '@pickleballinc/features/src/hub-cards/SkeletonArticleCard';
import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';

import { BlogNavigationTabs } from '@/modules/blog/BlogNavigationTabs';
import { BlogPagination } from '@/modules/blog/components/BlogPagination';
import { PAGE_SIZE } from '@/modules/blog/constants';
import { Category } from '@/types/blog.types';
import { getArticles } from '@/utils/actions/blog/getArticles';

interface PaginationContentProps {
  categorySlug: string;
  currentPage: string;
  category: Category;
}

export const PaginationContent = ({
  categorySlug,
  currentPage,
  category,
}: PaginationContentProps) => {
  const searchParams = useSearchParams();
  const subCategory = searchParams?.get('subcategory') || '';

  const { data, isFetching } = useQuery({
    queryKey: ['articles', categorySlug, currentPage, subCategory],
    queryFn: async () =>
      await getArticles({
        slug: categorySlug,
        subCategorySlug: subCategory,
        page: Number(currentPage),
      }),
    keepPreviousData: true,
  });

  const articles = data?.data?.articles || [];
  const totalPages = data?.data?.totalRecords ? Math.ceil(data.data.totalRecords / PAGE_SIZE) : 1;

  const possibleSubcategories = category?.subcategories?.filter(
    (subcategory) => +subcategory.articleCount > 0
  );

  return (
    <>
      {possibleSubcategories.length > 0 && (
        <BlogNavigationTabs
          subcategories={possibleSubcategories || []}
          categorySlug={categorySlug}
          allUrl={`/${categorySlug}/1`}
          isPaginatedView
        />
      )}

      {articles.length === 0 && !isFetching ? (
        <div className='w-full max-w-screen-sm p-4 mx-auto'>
          <NoDataFound
            title='No articles found!'
            subtitle='There are no articles in this subcategory.'
          />
        </div>
      ) : (
        <div className='px-2 pt-2 sm:px-4 sm:pt-4'>
          <div className='mb-4 grid justify-center gap-4 sm:gap-y-10 grid-cols-2 sm:grid-cols-3'>
            {isFetching
              ? Array.from({ length: 6 }).map((_, index) => <SkeletonArticleCard key={index} />)
              : articles.map((article) => (
                  <CustomLink
                    href={`/${categorySlug}/${article.slug}`}
                    key={`article-${article.slug}`}
                    className='flex justify-center'
                  >
                    <ArticleCard
                      imageUrl={article.imageUrl}
                      alt={article.imageAltText}
                      title={article.title}
                      type='paginationCover'
                      showDate
                      publishedAt={article.publishDateDisplayed}
                    />
                  </CustomLink>
                ))}
          </div>
          {articles.length > 0 && (
            <div className='mb-4 sm:mt-4 sm:px-4'>
              <BlogPagination
                currentPage={currentPage}
                totalPages={totalPages}
                categorySlug={categorySlug}
                subCategory={subCategory}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};
